/**
 * 環境別ログユーティリティ
 *
 * 開発環境（dev）でのみデバッグログを出力し、
 * ステージング（stg）および本番環境（prd）では機密情報を含むログを抑制します。
 *
 * セキュリティ要件:
 * - 認証トークンやIDトークンペイロードは本番環境で出力しない
 * - エラーログと警告ログは全環境で出力（運用監視に必要）
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * 現在の実行環境を取得
 * REACT_APP_ENVIRONMENT: dev, stg, prd
 */
const getEnvironment = (): string => {
  return process.env.REACT_APP_ENVIRONMENT || 'production';
};

/**
 * 開発環境かどうかを判定
 */
const isDevelopment = (): boolean => {
  return getEnvironment() === 'dev';
};

/**
 * ログレベルごとの出力可否を判定
 */
const shouldLog = (level: LogLevel): boolean => {
  const env = getEnvironment();

  switch (level) {
    case 'debug':
    case 'info':
      // デバッグとインフォは開発環境のみ
      return env === 'dev';
    case 'warn':
    case 'error':
      // 警告とエラーは全環境で出力
      return true;
    default:
      return false;
  }
};

/**
 * 機密情報をマスクする（オプション機能）
 * 将来的に実装を強化できるよう、構造を用意
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const maskSensitiveData = (data: any): any => {
  // 現時点では機密データのログ出力自体を抑制するため、
  // マスク処理は実装しない
  return data;
};

/**
 * Logger インターフェース
 */
interface Logger {
  debug: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
}

/**
 * ロガーインスタンスの作成
 */
export const logger: Logger = {
  /**
   * デバッグログ（開発環境のみ）
   * 認証フロー、API通信、セッション管理などのデバッグに使用
   */
  debug: (...args: any[]): void => {
    if (shouldLog('debug')) {
      console.log('[DEBUG]', ...args);
    }
  },

  /**
   * 情報ログ（開発環境のみ）
   * アプリケーションの状態変化や重要なイベントを記録
   */
  info: (...args: any[]): void => {
    if (shouldLog('info')) {
      console.info('[INFO]', ...args);
    }
  },

  /**
   * 警告ログ（全環境）
   * 潜在的な問題や予期しない動作を記録
   */
  warn: (...args: any[]): void => {
    if (shouldLog('warn')) {
      console.warn('[WARN]', ...args);
    }
  },

  /**
   * エラーログ（全環境）
   * エラーや例外を記録。運用監視に必要
   */
  error: (...args: any[]): void => {
    if (shouldLog('error')) {
      console.error('[ERROR]', ...args);
    }
  },
};

/**
 * 環境情報を出力するヘルパー関数（開発用）
 */
export const logEnvironmentInfo = (): void => {
  if (isDevelopment()) {
    console.log('🔧 [Environment Info]', {
      REACT_APP_ENVIRONMENT: getEnvironment(),
      NODE_ENV: process.env.NODE_ENV,
      isDevelopment: isDevelopment(),
    });
  }
};

/**
 * デフォルトエクスポート
 */
export default logger;
